import { Image, ScrollView, View, useWindowDimensions } from "react-native";
import type { ImageSourcePropType } from "react-native";
import { Dialog, DialogHeader } from "@louise/moon-ui";

type DetailDialogProps = {
  visible: boolean;
  title: string;
  icon?: ImageSourcePropType;
  onClose: () => void;
  children: React.ReactNode;
};

export default function DetailDialog({
  visible,
  title,
  icon,
  onClose,
  children,
}: DetailDialogProps) {
  const { height: windowHeight } = useWindowDimensions();
  const maxHeight = Math.max(360, windowHeight - 48);

  // React Native Web can leave a hidden Modal layer in the stacking context.
  // Unmount the detail panel while a child action dialog is active so it
  // cannot cover the confirmation surface.
  if (!visible) return null;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      // Match the bounded specimen-detail dialog in Cryo Vault.
      containerStyle={{ maxHeight }}
      surfaceStyle={{ maxHeight }}
      // Dialog's default horizontal inset is useful for compact alerts, but
      // this shell owns its own 24px header/content inset. Override it
      // explicitly so the two insets do not stack and narrow the form.
      contentStyle={{
        paddingTop: 0,
        paddingHorizontal: 0,
        paddingBottom: 0,
        maxHeight,
      }}
      testID="you-detail-dialog"
    >
      <View style={{ maxHeight }}>
        <View
          testID="you-detail-panel-title"
          style={{
            paddingTop: 24,
            paddingHorizontal: 24,
            paddingBottom: 20,
          }}
        >
          <DialogHeader
            leading={
              icon ? (
                <Image
                  source={icon}
                  // These icons already carry the muted neutral palette used
                  // by the You overview. Applying the legacy sepia filter
                  // here would turn the dialog header into a different icon
                  // system.
                  style={{ width: 24, height: 24 }}
                />
              ) : null
            }
            title={title}
          />
        </View>

        <ScrollView
          style={{ maxHeight: maxHeight - 76, flexShrink: 1 }}
          contentContainerStyle={{
            paddingHorizontal: 24,
            paddingBottom: 24,
          }}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </ScrollView>
      </View>
    </Dialog>
  );
}
